import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  Checkbox,
  Switch,
  TextField,
  MenuItem,
  Paper,
  Autocomplete,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { getEndingPositions } from '../../features/leaderboard/leaderboardSlice';
import {
  getRankingLookup,
  getRankingPositions,
  saveRankingMapping,
  exportRankingPositions
} from '../../features/ranking/rankingSlice';
import { useAlert } from '../../contexts/alertContext';
import GreatDatePicker from '../ui/forms/fields/datePicker';
import moment from 'moment';

// The ranking system accepts plain names for category, format and position, and
// a name or numeric id for category type. Options arrive from the lookup API in
// whatever shape it settles on, so flatten them to the names we send.
const toOptionNames = (list) =>
  (Array.isArray(list) ? list : [])
    .map((item) => (typeof item === 'string' ? item : item?.name || item?.label || ''))
    .filter(Boolean);

// A rejected thunk carries our `{ message, errors }` shape, but the shared
// leaderboard thunk still rejects with a bare string. Accept either.
const messageOf = (payload, fallback) => {
  if (!payload) return fallback;
  if (typeof payload === 'string') return payload;
  return payload.message || fallback;
};

const describeRow = (row) => {
  if (!row) return 'Unknown row';
  const name = [row.firstName, row.lastName].filter(Boolean).join(' ') || row.teamName || '—';
  return row.bsaNumber ? `${name} (${row.teamName}) - BSA: ${row.bsaNumber}` : `${name} (${row.teamName})`;
};

/**
 * What a knockout round's winner and loser are placed as, when we can tell from
 * the section's name what the round is.
 *
 * Every round except the final gives both its entrants the same position: the
 * floor that reaching that round guarantees. A quarter-finalist is at worst in
 * the last 8, a semi-finalist at worst 3rd. A winner who goes on to win the next
 * round is lifted out of this floor by that later round's own entry - the export
 * keeps a team's best position across every round it appears in.
 */
const ROUND_SUGGESTIONS = {
  final: { winnerPosition: '1st Place', loserPosition: '2nd Place' },
  semi: { winnerPosition: '3rd Place', loserPosition: '3rd Place' },
  quarter: { winnerPosition: 'Last 8', loserPosition: 'Last 8' },
  last16: { winnerPosition: 'Last 16', loserPosition: 'Last 16' },
  // There is no "Last 32" or "Last 4" in the ranking vocabulary, so rounds
  // deeper than the last 16 place nobody.
  last32: { winnerPosition: 'Rest', loserPosition: 'Rest' },
  playoff: { winnerPosition: 'Rest', loserPosition: 'Rest' },
  plate: { winnerPosition: 'Rest', loserPosition: 'Rest' }
};

const ROUND_WORDS = [
  ['final', /\bfinals?\b/],
  ['semi', /\bsemi[-\s]?finals?\b|\bsemis\b|\bsf\b/],
  ['quarter', /\bquarter[-\s]?finals?\b|\bquarters\b|\bqf\b/],
  ['last16', /\blast\s*16\b|\br16\b/],
  ['last32', /\blast\s*32\b|\br32\b/],
  ['playoff', /\bplay[-\s]?offs?\b/]
];

// Consolation trees. "Cup counts, Plate is Rest" is the agreed rule, so nothing
// in one of these ranks however far down it goes.
const CONSOLATION = /\b(plate|bowl|shield|consolation)\b/;

/**
 * The round a section's name suggests, or null when it suggests none.
 *
 * Detection is only ever a starting point - section names are free text and the
 * same round is spelled "Semi Final 1", "Semi-Finals" and "Section 1 -
 * Semi-Finals" across the events we hold - so the organiser confirms or
 * overrides every row, and the result is saved to the event.
 *
 * A round word must be present on its own. "Cup" and "Plate" alone are division
 * names, not rounds: `Wingate (Testing)` has both as 40-team round-robin
 * sections, and classifying those as knockout rounds would place every team in
 * them as Rest.
 */
const detectRound = (name) => {
  const normalised = String(name || '').toLowerCase();
  const match = ROUND_WORDS.find(([, pattern]) => pattern.test(normalised));

  if (!match) return null;
  if (CONSOLATION.test(normalised)) return 'plate';

  return match[0];
};

/**
 * A starting classification for every section we can read a round from. Saved
 * mappings take precedence over this - see the dialog's reset effect.
 */
const suggestMapping = (sections) => {
  const mapping = {};

  sections.forEach((section) => {
    const round = detectRound(section.name);
    if (round) mapping[section.id] = { ...ROUND_SUGGESTIONS[round] };
  });

  return mapping;
};

const PBARankingDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { showAlertError } = useAlert();
  const event = useSelector((state) => state.event.event);
  const lookup = useSelector((state) => state.ranking.lookup);
  const positions = useSelector((state) => state.ranking.positions);
  const positionsCategory = useSelector((state) => state.ranking.positionsCategory);
  const sections = event?.sections || [];

  const [step, setStep] = useState(0);
  const [editablePositions, setEditablePositions] = useState([]);
  // Snapshot of the rows as submitted - results are mapped against this rather
  // than live state, so edits made after a send cannot misattribute an error.
  const [submittedRows, setSubmittedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  // Phase-1 safety default: the export still reports every bowler as "Rest" for
  // events whose knockout stage is modelled as sections, so nothing reaches the
  // ranking system unless the organiser turns this off deliberately.
  const [dryRun, setDryRun] = useState(true);
  // `{ [sectionId]: { winnerPosition, loserPosition } }` for the sections the
  // organiser has classified as knockout rounds. A section absent from this is a
  // round-robin section and behaves exactly as it always has.
  const [sectionMapping, setSectionMapping] = useState({});

  const schema = yup.object().shape({
    exportType: yup.string().required(),
    selectedSections: yup.array().when('exportType', {
      is: 'section-combination',
      then: (schema) => schema.min(1, 'Select at least one section'),
      otherwise: (schema) => schema
    }),
    eventDate: yup.string().required('Event date is required'),
    category: yup.string().trim().required('Category is required'),
    format: yup.string().trim().required('Format is required'),
    categoryType: yup.string().trim().required('Category type is required')
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    trigger,
    formState: { errors }
  } = useForm({
    defaultValues: {
      exportType: 'bracket',
      selectedSections: sections.map((s) => s.id),
      eventDate: null,
      category: '',
      format: '',
      categoryType: '',
      district: '',
      districtId: ''
    },
    resolver: yupResolver(schema)
  });

  const steps = [
    'Event Parameters',
    'Sections',
    'Verify & Send',
    dryRun ? 'Dry Run Result' : 'Import Result'
  ];

  useEffect(() => {
    if (open) dispatch(getRankingLookup());
  }, [open, dispatch]);

  const selectedCategory = watch('category');

  // Positions are per category, so the list is refetched when the category
  // changes. Until one is chosen the export keeps the plain lookup list.
  useEffect(() => {
    if (open && selectedCategory) dispatch(getRankingPositions(selectedCategory));
  }, [open, selectedCategory, dispatch]);

  useEffect(() => {
    if (open) {
      setStep(0);
      setImportResult(null);
      setSubmitError(null);
      setEditablePositions([]);
      setSubmittedRows([]);

      // The classification is saved to the event, so it is set up once and
      // reused. With none saved yet, fall back to what the section names
      // suggest - the organiser confirms or corrects it below.
      const saved =
        event?.rankingExport && typeof event.rankingExport === 'object'
          ? event.rankingExport
          : null;
      setSectionMapping(
        saved && Object.keys(saved).length > 0 ? saved : suggestMapping(sections)
      );

      reset({
        exportType: 'bracket',
        selectedSections: sections.map((s) => s.id),
        eventDate: event?.startDate ? moment(event.startDate) : null,
        category: '',
        format: '',
        categoryType: '',
        district: '',
        districtId: ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event?.startDate]);

  const categoryOptions = toOptionNames(lookup?.categories);
  const formatOptions = toOptionNames(lookup?.formats);
  const categoryTypeOptions = toOptionNames(lookup?.categoryTypes);
  const districtOptions = Array.isArray(lookup?.districts) ? lookup.districts : [];

  // Positions are per category. Guard on `positionsCategory` as well as the
  // fetched list: after a category change the previous category's positions are
  // still in state for the moment before the new ones arrive, and offering them
  // would let the organiser pick a value that category does not accept.
  const scopedPositions =
    positionsCategory && positionsCategory === selectedCategory ? positions : null;

  const lookupPositions = toOptionNames(scopedPositions || lookup?.positions);

  // Anything already proposed by the detection or saved on the event must stay
  // selectable even if the lookup does not list it, or the organiser would see a
  // blank dropdown they cannot correct.
  const positionOptions = Array.from(
    new Set([
      ...lookupPositions,
      ...Object.values(sectionMapping).flatMap((m) => [m.winnerPosition, m.loserPosition]),
      ...editablePositions.map((p) => p.position)
    ])
  ).filter(Boolean);

  // Once the ranking system's own position names are known, anything the export
  // generated that is not on that list will be rejected row by row - so say so
  // before the send rather than after.
  const knownPositions = new Set(lookupPositions);
  const isUnknownPosition = (position) =>
    knownPositions.size > 0 && !!position && !knownPositions.has(position);
  const unknownPositionCount = editablePositions.filter((p) =>
    isUnknownPosition(p.position)
  ).length;

  // The mapping dropdowns are `select`, not freeSolo, so their options ARE the
  // constraint. They offer the selected category's positions and nothing else:
  // the ranking system now scopes positions by category, so a round mapped to a
  // position the category does not publish fails every row it produces - and
  // its error blames the position, which is the one field that is correct.
  //
  // A field's own stored value rides along even once the category no longer
  // offers it. Changing category has to show the organiser what has gone stale;
  // dropping it would blank the field and read as the mapping having been lost.
  const mappingOptions = (value) =>
    Array.from(new Set([...lookupPositions, ...(value ? [value] : [])])).filter(Boolean);

  // True once a value is set to something the current category does not list.
  const isStaleMappingValue = (value) => !!value && isUnknownPosition(value);

  // Step 0 -> 1. Only this step's own fields are validated, deliberately not the
  // whole schema: `selectedSections` is checked on step 1 (it is only required
  // for a section-combination export) and its error renders there, so validating
  // it here would block Next and show the organiser nothing that explains why.
  const onNext = async () => {
    const ok = await trigger(['eventDate', 'category', 'format', 'categoryType']);
    if (ok) setStep(1);
  };

  // Step 1: Generate List
  const onGenerate = async (data) => {
    setLoading(true);
    setSubmitError(null);

    try {
      // How the sections are classified is what the positions are derived from,
      // so it is sent with the request rather than read back from the event -
      // no window where a just-made change has not been written yet.
      const rows = await dispatch(
        getEndingPositions({
          eventId: event.id,
          exportType: data.exportType,
          selectedSections: data.selectedSections,
          sectionMapping
        })
      ).unwrap();

      // Persisted for next time. Best-effort on purpose: failing to save the
      // classification must not cost the organiser the list they just generated,
      // and the export above has already used it.
      dispatch(saveRankingMapping({ eventId: event.id, sectionMapping })).catch(() => {});

      setEditablePositions(rows.map((row) => ({ ...row })));
      setStep(2);
    } catch (error) {
      const message = messageOf(error, 'Could not generate the list of ending positions.');
      setSubmitError({ message, errors: [] });
      showAlertError(message);
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (index, changes) => {
    setEditablePositions((current) =>
      current.map((row, i) => (i === index ? { ...row, ...changes } : row))
    );
  };

  const moveRow = (index, direction) => {
    setEditablePositions((current) => {
      const next = [...current];
      const target = index + direction;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  // A section is a knockout round only while it has an entry here; removing the
  // entry is what returns it to being an ordinary round-robin section.
  const setSectionIsKnockout = (section, isKnockout) => {
    setSectionMapping((current) => {
      const next = { ...current };

      if (!isKnockout) {
        delete next[section.id];
        return next;
      }

      const round = detectRound(section.name);
      next[section.id] = round
        ? { ...ROUND_SUGGESTIONS[round] }
        : { winnerPosition: '', loserPosition: '' };

      return next;
    });
  };

  const setSectionPosition = (sectionId, field, value) => {
    setSectionMapping((current) => ({
      ...current,
      [sectionId]: { ...current[sectionId], [field]: value }
    }));
  };

  const knockoutSections = sections.filter((section) => sectionMapping[section.id]);

  // A position with a blank value would send a row the ranking system cannot
  // place, so generating is held until every classified round is complete.
  const incompleteMappingCount = knockoutSections.filter(
    (section) =>
      !sectionMapping[section.id].winnerPosition || !sectionMapping[section.id].loserPosition
  ).length;

  // Two rounds both awarding 1st Place is almost always a parallel-tree event
  // where only one tree counts - "Cup counts, Plate is Rest". Worth naming
  // rather than silently letting the lower-ranked tree overwrite the Cup.
  const firstPlaceClaimants = knockoutSections.filter(
    (section) => sectionMapping[section.id].winnerPosition === '1st Place'
  );

  // Step 1: Send
  const onSend = async (formData) => {
    if (editablePositions.length === 0) {
      showAlertError('There are no rows to export.');
      return;
    }

    const alreadyImported = importResult && !importResult.dryRun && importResult.successCount > 0;
    if (
      alreadyImported &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        'These rows have already been imported. Sending again may duplicate them in the ranking system. Continue?'
      )
    ) {
      return;
    }

    setSending(true);
    setSubmitError(null);

    const rows = editablePositions.map((row) => ({
      memberId: row.memberId ?? null,
      teamId: row.teamId,
      teamName: row.teamName,
      firstName: row.firstName,
      lastName: row.lastName,
      bsaNumber: row.bsaNumber,
      position: row.position
    }));

    const payload = {
      dryRun,
      district: formData.district || undefined,
      districtId: formData.districtId || undefined,
      details: {
        event: event.name,
        eventDate: moment(formData.eventDate).format('YYYY-MM-DD'),
        category: formData.category,
        format: formData.format,
        categoryType: formData.categoryType
      },
      rows
    };

    try {
      const result = await dispatch(
        exportRankingPositions({ eventId: event.id, ...payload })
      ).unwrap();

      setSubmittedRows(rows);
      setImportResult(result);
      setStep(3);
    } catch (error) {
      const message = messageOf(error, 'Could not reach the ranking system.');
      setSubmitError({
        message,
        errors: Array.isArray(error?.errors) ? error.errors : []
      });
      showAlertError(message);
    } finally {
      setSending(false);
    }
  };

  // Map a global rowIndex from the API back to the row the organiser saw.
  const rowFor = (rowIndex) =>
    Number.isInteger(rowIndex) ? submittedRows[rowIndex] : null;

  const errorMessageFor = (rowIndex) =>
    submitError?.errors?.find((entry) => entry.rowIndex === rowIndex)?.message ||
    (rowIndex === null ? submitError?.errors?.find((entry) => entry.rowIndex === null)?.message : null);

  // Every row rejected, yet every one of those rows used a position the selected
  // category genuinely publishes. That rules the POSITION out and nothing else:
  // the message names position, category and format together, and the category
  // and the format can each reject an entire export on their own. Measured
  // 2026-09-30: the format `Champ of Champions` rejects every row under every
  // category, so blaming the category alone would send an organiser to change
  // the one field that is already correct.
  //
  // Derived rather than matched against a list of categories known to be broken.
  // Which categories import changes without notice - District Masters went from
  // rejecting every row to importing cleanly inside a single day - so a
  // hardcoded list would go stale and mislead exactly when it mattered.
  const rejectedRows = importResult
    ? [...(importResult.errors || []), ...(importResult.skipRows || [])]
    : [];

  const positionIsNotTheProblem =
    !!importResult &&
    // Without a loaded position list nothing can be ruled out, so the claim
    // would be a guess - `isUnknownPosition` is vacuously false in that state.
    knownPositions.size > 0 &&
    (importResult.summary?.willBeImported ?? 0) === 0 &&
    rejectedRows.length > 0 &&
    rejectedRows.every((entry) => {
      const row = rowFor(entry.rowIndex);
      return !!row && !isUnknownPosition(row.position);
    });

  const renderField = (name, label, options, helper) => (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Autocomplete
          freeSolo
          options={options}
          value={field.value || ''}
          onChange={(e, newValue) => field.onChange(newValue || '')}
          onInputChange={(e, newInput, reason) => {
            if (reason === 'input') field.onChange(newInput);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              margin="dense"
              error={!!errors[name]}
              helperText={errors[name]?.message || helper}
            />
          )}
        />
      )}
    />
  );

  const renderResult = () => {
    if (!importResult) return <Typography>No summary available.</Typography>;

    const { dryRun: wasDryRun, summary, errors: rowErrors, skipRows, chunks } = importResult;
    const unattributed = (rowErrors || []).filter((entry) => entry.rowIndex === null);

    // One record per row, echoed back with the ids and names the ranking system
    // resolved for it. Sent by a dry run as well as a real import, which is why
    // the table is gated on there being records rather than on `!wasDryRun`.
    //
    // What differs is *what it could resolve*, and that is what the columns
    // follow (see `hasValue` below). A dry run writes nothing and matches no
    // player, so `playerId`, `points` and `gender` come back null while the
    // lookup-derived ids and `tier` are populated. On a real import `playerId`
    // is the only place their own player id appears - and their importer accepts
    // any BSA number without checking it against a register, so a
    // wrong-but-well-formed number attaches a result to the wrong player
    // silently. That id next to the number we sent is what makes it visible.
    const importedRows = Array.isArray(importResult.imported) ? importResult.imported : [];
    const cell = (value) => (value === null || value === undefined || value === '' ? '—' : value);

    // A column earns its place only if some row has something in it. On a dry
    // run that drops Points and Player ID instead of printing 65 em dashes, and
    // it keeps working if the vendor starts filling them in earlier.
    const hasValue = (key) =>
      importedRows.some((record) => record[key] !== null && record[key] !== undefined);

    return (
      <>
        {wasDryRun ? (
          <Alert severity={summary?.willBeImported > 0 ? 'info' : 'warning'} sx={{ mb: 2 }}>
            <strong>Dry run — nothing was sent to the ranking system.</strong>
            <br />
            {summary?.willBeImported ?? 0} of{' '}
            {(summary?.willBeImported ?? 0) + (summary?.willBeSkipped ?? 0)} rows would be imported
            {summary?.foundDuplicates > 0 ? `, with ${summary.foundDuplicates} duplicates found` : ''}
            .
          </Alert>
        ) : importResult.failCount > 0 || importResult.failedChunks > 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>
              {importResult.successCount} of {importResult.totalRows} rows imported.
            </strong>
            <br />
            The import is not transactional, so the rows listed below were <strong>not</strong>{' '}
            imported. The rest were.
          </Alert>
        ) : (
          <Alert severity="success" sx={{ mb: 2 }}>
            <strong>Import complete.</strong>
            <br />
            {importResult.successCount} rows imported.
          </Alert>
        )}

        {positionIsNotTheProblem && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Every row was rejected, and every one used a position this category publishes — so the
            position is not what is wrong. The message names position, category and format together,
            and either of the other two can reject an entire export on its own: swap the category,
            and if that changes nothing, swap the format.
          </Alert>
        )}

        {wasDryRun && (skipRows || []).length > 0 && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Rows that would be skipped
            </Typography>
            {(skipRows || []).map((entry, index) => (
              <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                {describeRow(rowFor(entry.rowIndex))} — {entry.reason}
              </Typography>
            ))}
          </Paper>
        )}

        {!wasDryRun && (rowErrors || []).length > 0 && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Rows not imported
            </Typography>
            {(rowErrors || [])
              .filter((entry) => entry.rowIndex !== null)
              .map((entry, index) => (
                <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                  {describeRow(rowFor(entry.rowIndex))} — {entry.message}
                </Typography>
              ))}
            {unattributed.length > 0 && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Unattributed errors
                </Typography>
                {unattributed.map((entry, index) => (
                  <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                    {entry.message}
                  </Typography>
                ))}
              </>
            )}
          </Paper>
        )}

        {!wasDryRun && importResult.chunkCount > 1 && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Batches
            </Typography>
            {(chunks || []).map((chunk) => (
              <Typography
                key={chunk.index}
                variant="body2"
                sx={{ mb: 0.5 }}
                color={chunk.ok ? 'text.primary' : 'error.main'}>
                Batch {chunk.index + 1} of {importResult.chunkCount} (rows {chunk.offset + 1}–
                {chunk.offset + chunk.rowCount}):{' '}
                {chunk.outcome === 'ok'
                  ? `${chunk.successCount} imported, ${chunk.failCount} failed`
                  : chunk.message}
              </Typography>
            ))}
          </Paper>
        )}

        {!wasDryRun && (importResult.failedChunks > 0 || (chunks || []).some((c) => c.outcome === 'unknown')) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            One or more batches could not be confirmed. Check the ranking system before sending
            again — rows from an unconfirmed batch may already be there.
          </Alert>
        )}

        {importedRows.length > 0 && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {wasDryRun
                ? `Would be imported (${importedRows.length})`
                : `Imported (${importedRows.length})`}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {wasDryRun
                ? 'What the ranking system resolved for each row. Nothing has been written yet, so no player has been matched and no points assigned.'
                : 'What the ranking system stored, as it stored it. Player ID is its own — check it resolved the bowler you expected.'}
            </Typography>
            <TableContainer sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Bowler</TableCell>
                    <TableCell>BSA No.</TableCell>
                    <TableCell>Position</TableCell>
                    {hasValue('points') && <TableCell align="right">Points</TableCell>}
                    {hasValue('tier') && <TableCell align="right">Tier</TableCell>}
                    {hasValue('playerId') && <TableCell align="right">Player ID</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importedRows.map((record) => (
                    <TableRow key={`${record.rowIndex}-${record.bsaNumber}`}>
                      <TableCell>
                        {cell(`${record.firstName || ''} ${record.lastName || ''}`.trim())}
                      </TableCell>
                      <TableCell>{cell(record.bsaNumber)}</TableCell>
                      <TableCell>{cell(record.position)}</TableCell>
                      {hasValue('points') && (
                        <TableCell align="right">{cell(record.points)}</TableCell>
                      )}
                      {hasValue('tier') && <TableCell align="right">{cell(record.tier)}</TableCell>}
                      {hasValue('playerId') && (
                        <TableCell align="right">{cell(record.playerId)}</TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2">
            <strong>Ranking event:</strong> {importResult.eventId}
          </Typography>
          <Typography variant="body2">
            <strong>Rows:</strong> {importResult.totalRows} in {importResult.chunkCount} batch
            {importResult.chunkCount === 1 ? '' : 'es'}
          </Typography>
          {importResult.message && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {importResult.message}
            </Typography>
          )}
        </Paper>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" color="primary" onClick={onClose}>
            Close
          </Button>
          {wasDryRun && (
            <Button
              variant="outlined"
              color="primary"
              disabled={sending}
              onClick={() => {
                setDryRun(false);
                setStep(2);
              }}>
              Send for real
            </Button>
          )}
        </Box>
      </>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>PBA Ranking Export</DialogTitle>
      <DialogContent>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {event?.name}
        </Typography>
        <Stepper activeStep={step} sx={{ mb: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {step === 0 && (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              onNext();
            }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Event ranking parameters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              How this event is filed with the ranking system. The category decides which
              positions you can choose from when you classify the sections next.
            </Typography>

            <GreatDatePicker
              name="eventDate"
              label="Event Date"
              control={control}
              format={'DD/MM/YYYY'}
              sx={{ mr: 1 }}
              error={!!errors.eventDate}
              helperText={errors.eventDate?.message}
            />

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
              <Box sx={{ flex: '1 1 180px' }}>
                {renderField('category', 'Category', categoryOptions)}
              </Box>
              <Box sx={{ flex: '1 1 180px' }}>{renderField('format', 'Format', formatOptions)}</Box>
              <Box sx={{ flex: '1 1 180px' }}>
                {renderField('categoryType', 'Category Type', categoryTypeOptions)}
              </Box>
            </Box>

            <Box sx={{ mt: 2, maxWidth: 400 }}>
              <Controller
                name="district"
                control={control}
                render={({ field }) => (
                  <Autocomplete
                    options={districtOptions}
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : option?.district || ''
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?.districtId === value?.districtId
                    }
                    value={districtOptions.find((d) => d.district === field.value) || null}
                    onChange={(e, newValue) => {
                      field.onChange(newValue?.district || '');
                      setValue('districtId', newValue?.districtId || '');
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="District (optional)" margin="dense" />
                    )}
                  />
                )}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="contained" color="primary" type="submit">
                Next
              </Button>
            </Box>
          </Box>
        )}

        {step === 1 && (
          <Box component="form" onSubmit={handleSubmit(onGenerate)}>
            <Typography>Select the type of export:</Typography>
            <FormControl component="fieldset" sx={{ mt: 2 }}>
              <Controller
                name="exportType"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field} row>
                    <FormControlLabel value="bracket" control={<Radio />} label="Bracket" />
                    <FormControlLabel
                      value="section-combination"
                      control={<Radio />}
                      label="Section Combination"
                    />
                  </RadioGroup>
                )}
              />
            </FormControl>
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1">Sections</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                A section that holds one game per round is a knockout round. Say what its winner
                and its loser placed as, and the standings come from the game rather than from a
                round-robin table. Everything left as a round robin keeps its section positions.
                This is saved to the event.
              </Typography>

              <Controller
                name="selectedSections"
                control={control}
                render={({ field }) => {
                  // Every section is offered, finalised or not. A section
                  // that has not been finalised has no `finalisedLog`, so
                  // the export derives its standings instead - the organiser
                  // is no longer blocked on finalising before submitting.
                  if (sections.length === 0) {
                    return (
                      <Typography color="warning.main" sx={{ mt: 2 }}>
                        This event has no sections.
                      </Typography>
                    );
                  }

                  return (
                    <Box sx={{ maxHeight: 320, overflowY: 'auto', pr: 1 }}>
                      {sections.map((section) => {
                        const mapping = sectionMapping[section.id];
                        const included = field.value.includes(section.id);

                        return (
                          <Box
                            key={section.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              flexWrap: 'wrap',
                              py: 0.5,
                              borderBottom: '1px solid',
                              borderColor: 'divider'
                            }}>
                            <FormControlLabel
                              sx={{ minWidth: 200, flex: '1 1 200px', m: 0 }}
                              control={
                                <Checkbox
                                  size="small"
                                  checked={included}
                                  onChange={() => {
                                    field.onChange(
                                      included
                                        ? field.value.filter((sid) => sid !== section.id)
                                        : [...field.value, section.id]
                                    );
                                  }}
                                />
                              }
                              label={section.name}
                            />

                            <FormControlLabel
                              sx={{ m: 0, minWidth: 150 }}
                              control={
                                <Switch
                                  size="small"
                                  checked={Boolean(mapping)}
                                  onChange={(e) => setSectionIsKnockout(section, e.target.checked)}
                                />
                              }
                              label={<Typography variant="body2">Knockout</Typography>}
                            />

                            {mapping && (
                              <>
                                <TextField
                                  select
                                  size="small"
                                  label="Winner"
                                  value={mapping.winnerPosition}
                                  onChange={(e) =>
                                    setSectionPosition(section.id, 'winnerPosition', e.target.value)
                                  }
                                  error={isStaleMappingValue(mapping.winnerPosition)}
                                  helperText={
                                    isStaleMappingValue(mapping.winnerPosition)
                                      ? 'Not offered by this category'
                                      : ''
                                  }
                                  sx={{ minWidth: 150 }}>
                                  {mappingOptions(mapping.winnerPosition).map((option) => (
                                    <MenuItem key={option} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                                </TextField>
                                <TextField
                                  select
                                  size="small"
                                  label="Loser"
                                  value={mapping.loserPosition}
                                  onChange={(e) =>
                                    setSectionPosition(section.id, 'loserPosition', e.target.value)
                                  }
                                  error={isStaleMappingValue(mapping.loserPosition)}
                                  helperText={
                                    isStaleMappingValue(mapping.loserPosition)
                                      ? 'Not offered by this category'
                                      : ''
                                  }
                                  sx={{ minWidth: 150 }}>
                                  {mappingOptions(mapping.loserPosition).map((option) => (
                                    <MenuItem key={option} value={option}>
                                      {option}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              </>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  );
                }}
              />

              {errors.selectedSections && (
                <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                  {errors.selectedSections.message}
                </Typography>
              )}

              {incompleteMappingCount > 0 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {incompleteMappingCount} knockout round
                  {incompleteMappingCount === 1 ? ' has' : 's have'} no position set for the winner
                  or the loser. Pick both before generating, or those rows go out unplaced.
                </Alert>
              )}

              {firstPlaceClaimants.length > 1 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {firstPlaceClaimants.length} rounds award 1st Place:{' '}
                  {firstPlaceClaimants.map((section) => section.name).join(', ')}. Where a
                  tournament runs a Cup and a Plate, only the Cup places — set the others to Rest.
                </Alert>
              )}
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={dryRun}
                  onChange={(e) => setDryRun(e.target.checked)}
                  color="primary"
                />
              }
              label="Dry run (validate only, nothing is imported)"
              sx={{ mt: 2, display: 'block' }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading || incompleteMappingCount > 0}>
                {loading ? 'Generating…' : 'Generate List'}
              </Button>
            </Box>
          </Box>
        )}

        {step === 2 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Review the list before sending
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {editablePositions.length} bowler
              {editablePositions.length === 1 ? '' : 's'} will be sent
              {editablePositions.length > 100
                ? ` in ${Math.ceil(editablePositions.length / 100)} batches`
                : ''}
              . {dryRun ? 'This is a dry run — nothing will be imported.' : ''}
            </Typography>

            {unknownPositionCount > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {unknownPositionCount} row{unknownPositionCount === 1 ? '' : 's'} use a position
                name the ranking system does not recognise. Pick a valid one from the dropdown
                before sending, or those rows will be rejected.
              </Alert>
            )}

            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError.message}
              </Alert>
            )}

            <Box sx={{ maxHeight: 400, overflowY: 'auto', mb: 3, px: 2 }}>
              {editablePositions.length === 0 ? (
                <Typography>
                  No positions were generated for this export. Check that the selected sections
                  have teams with BSA numbers.
                </Typography>
              ) : (
                editablePositions.map((pos, idx) => (
                  <Box
                    key={pos.memberId || `${pos.teamId}-${idx}`}
                    sx={{
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      borderBottom:
                        idx < editablePositions.length - 1 ? '1px dashed #eee' : 'none'
                    }}>
                    <Autocomplete
                      freeSolo
                      options={positionOptions}
                      value={pos.position || ''}
                      onChange={(e, newValue) => updateRow(idx, { position: newValue || '' })}
                      onInputChange={(e, newInput, reason) => {
                        if (reason === 'input') updateRow(idx, { position: newInput });
                      }}
                      sx={{ width: 200, mr: 2 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          color={
                            errorMessageFor(idx) || isUnknownPosition(pos.position)
                              ? 'error'
                              : 'info'
                          }
                          error={!!errorMessageFor(idx) || isUnknownPosition(pos.position)}
                          helperText={
                            isUnknownPosition(pos.position) ? 'Not a known ranking position' : ''
                          }
                        />
                      )}
                    />
                    <Box sx={{ flex: 1 }}>
                      {pos.firstName} {pos.lastName} ({pos.teamName})
                      {pos.bsaNumber && ` - BSA: ${pos.bsaNumber}`}
                      {errorMessageFor(idx) && (
                        <Typography variant="caption" color="error" display="block">
                          {errorMessageFor(idx)}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      size="small"
                      disabled={idx === 0}
                      onClick={() => moveRow(idx, -1)}
                      sx={{ mx: 1 }}>
                      ↑
                    </Button>
                    <Button
                      size="small"
                      disabled={idx === editablePositions.length - 1}
                      onClick={() => moveRow(idx, 1)}>
                      ↓
                    </Button>
                  </Box>
                ))
              )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                color={dryRun ? 'inherit' : 'primary'}
                onClick={handleSubmit(onSend)}
                disabled={sending || editablePositions.length === 0}>
                {sending ? 'Sending…' : dryRun ? 'Validate (dry run)' : 'Send to PBA Ranking'}
              </Button>
            </Box>
          </Box>
        )}

        {step === 3 && <Box>{renderResult()}</Box>}
      </DialogContent>
      <DialogActions>
        {step > 0 && step < 3 && (
          <Button onClick={() => setStep(step - 1)} disabled={sending || loading}>
            Back
          </Button>
        )}
        <Button onClick={onClose} disabled={sending}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PBARankingDialog;
